import { useEffect, useRef, useState } from "react";
import { RotateCw } from "lucide-react";
import api from "@/lib/api";

const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let cargando = null;
const avisados = new Set();

// Cuenta en Eventos de seguridad los fallos del widget (una vez por código y página).
function avisarFallo(codigo) {
  if (avisados.has(codigo)) return;
  avisados.add(codigo);
  api.post("/auth/captcha-fallo", { codigo }).catch(() => {});
}

function cargarTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  cargando = cargando || new Promise((ok, mal) => {
    const s = document.createElement("script");
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => ok(window.turnstile);
    s.onerror = () => { cargando = null; mal(new Error("turnstile")); };
    document.head.appendChild(s);
  });
  return cargando;
}

// Cloudflare no pudo comprobar el navegador (p. ej. 600010): casi siempre una extensión, una VPN o el modo estricto de privacidad.
function AyudaCaptcha({ codigo, onReintentar }) {
  return (
    <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-800" data-testid="register-captcha-error">
      <p className="font-semibold">No se ha podido hacer la comprobación antirrobots.</p>
      <p className="mt-1">Suele pasar cuando una extensión del navegador (bloqueador de anuncios, VPN o privacidad) bloquea a Cloudflare. Para seguir:</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-4" data-testid="register-captcha-ayuda">
        <li>Desactiva las extensiones para esta página y pulsa «Reintentar».</li>
        <li>O abre el alta en otro navegador o en una ventana privada sin extensiones.</li>
        <li>Si sigue sin ir, prueba con otra conexión (por ejemplo, los datos del móvil).</li>
      </ul>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button type="button" onClick={onReintentar} className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-white px-3 py-1 font-semibold text-rose-800 transition-colors hover:bg-rose-100" data-testid="register-captcha-reintentar">
          <RotateCw className="h-3.5 w-3.5" /> Reintentar
        </button>
        {codigo && <span className="text-rose-700/80" data-testid="register-captcha-codigo">Código del error: {codigo}</span>}
      </div>
    </div>
  );
}

// Captcha Cloudflare Turnstile del alta. Cada token vale una vez: cambia `reinicio` para pedir otro.
export function Turnstile({ onToken, reinicio }) {
  const caja = useRef(null);
  const [error, setError] = useState(null);
  const [intento, setIntento] = useState(0);
  useEffect(() => {
    let id;
    let vivo = true;
    onToken("");
    setError(null);
    cargarTurnstile().then((ts) => {
      if (!vivo || !caja.current) return;
      id = ts.render(caja.current, {
        sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY, action: "registro", language: "es", theme: "light",
        callback: (t) => { setError(null); onToken(t); },
        "expired-callback": () => onToken(""),
        "error-callback": (codigo) => { onToken(""); setError(String(codigo || "")); avisarFallo(String(codigo || "otro")); return true; },
      });
    }).catch(() => { avisarFallo("carga"); if (vivo) setError(""); });
    return () => { vivo = false; if (id !== undefined) window.turnstile?.remove(id); };
  }, [reinicio, intento, onToken]);
  return (
    <div data-testid="register-captcha">
      <div ref={caja} className={error === "" ? "hidden" : "min-h-[65px]"} />
      {error !== null && <AyudaCaptcha codigo={error} onReintentar={() => setIntento((n) => n + 1)} />}
    </div>
  );
}
